import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import { Heart, Send, Smile, Meh, Frown, Check } from 'lucide-react';
import DictationInput from '../components/DictationInput';
import { storeReflection } from '../lib/aiReflections';

const MOOD_QUICK_OPTIONS = [
  { value: 'good', label: 'Good', icon: Smile, color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  { value: 'okay', label: 'Okay', icon: Meh, color: 'bg-amber-100 text-amber-700 border-amber-300' },
  { value: 'struggling', label: 'Struggling', icon: Frown, color: 'bg-red-100 text-red-700 border-red-300' },
];

const ACCOUNTABILITY_ITEMS = [
  { key: 'stayed_sober', label: 'Stayed sober today' },
  { key: 'applied_for_jobs', label: 'Applied for jobs' },
  { key: 'attended_work', label: 'Attended work' },
  { key: 'completed_training', label: 'Completed training' },
  { key: 'attended_peer_support', label: 'Attended peer support' },
  { key: 'met_with_mentor', label: 'Met with mentor' },
];

export default function CheckInPage() {
  const { axiosAuth } = useAuth();
  const [mood, setMood] = useState(5);
  const [moodQuick, setMoodQuick] = useState('');
  const [cravings, setCravings] = useState(0);
  const [goalsMet, setGoalsMet] = useState(false);
  const [notes, setNotes] = useState('');
  const [notesOriginal, setNotesOriginal] = useState('');
  const [goalPrompt, setGoalPrompt] = useState('What is one thing you want to strengthen in your life right now, and why does it matter to you?');
  const [accountability, setAccountability] = useState({
    stayed_sober: false,
    applied_for_jobs: false,
    attended_work: false,
    completed_training: false,
    attended_peer_support: false,
    met_with_mentor: false,
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const api = axiosAuth();

  useEffect(() => {
    const loadGoals = async () => {
      try {
        const res = await api.get('/goals');
        const active = (res.data || []).filter(g => g.status === 'active');
        const topGoal = active[0];
        if (topGoal?.title) {
          setGoalPrompt(`Thinking about your goal to ${topGoal.title}, what is one choice you made today that moved you closer to it?`);
        } else {
          setGoalPrompt('What is one thing you want to strengthen in your life right now, and why does it matter to you?');
        }
      } catch {
        setGoalPrompt('What is one thing you want to strengthen in your life right now, and why does it matter to you?');
      }
    };
    loadGoals();
  }, []);

  const moodLabels = ['', 'Struggling', 'Struggling', 'Struggling', 'Okay', 'Okay', 'Okay', 'Good', 'Good', 'Good', 'Good'];

  const toggleAccountability = (key) => {
    setAccountability(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await api.post('/checkins', {
        mood,
        cravings,
        goals_met: goalsMet,
        notes,
        mood_quick: moodQuick,
        ...accountability
      });
      toast.success('Check-in recorded! Keep up the great work!');
      if (notes?.trim()) {
        await storeReflection(api, {
          original_text: notesOriginal || notes,
          refined_text: notes,
          context: 'check_in',
          task_name: 'Daily Check-in Notes',
          task_description: `Mood: ${moodQuick || moodLabels[mood] || mood}. Goals met: ${goalsMet ? 'yes' : 'no'}.`,
          category: 'emotional',
          source: 'check_in'
        });
      }
      setSubmitted(true);
    } catch (err) {
      toast.error('Failed to submit check-in');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setMood(5);
    setMoodQuick('');
    setCravings(0);
    setGoalsMet(false);
    setNotes('');
    setNotesOriginal('');
    setAccountability({
      stayed_sober: false,
      applied_for_jobs: false,
      attended_work: false,
      completed_training: false,
      attended_peer_support: false,
      met_with_mentor: false,
    });
  };

  if (submitted) {
    return (
      <div className="max-w-lg mx-auto text-center py-16" data-testid="checkin-success">
        <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-4">
          <Heart className="w-8 h-8 text-teal-600" />
        </div>
        <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope' }}>Check-in Complete</h2>
        <p className="text-muted-foreground mb-6">Great job taking this step today. Every check-in matters.</p>
        <Button onClick={resetForm} variant="outline" className="rounded-full" data-testid="new-checkin-button">
          New Check-in
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto" data-testid="checkin-page">
      <h1 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Manrope' }}>Daily Check-in</h1>
      <p className="text-muted-foreground text-sm mb-6">How are you doing today?</p>

      <div className="space-y-4">
        {/* Quick Mood Selection */}
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardContent className="p-5">
            <Label className="text-sm font-medium mb-3 block">How are you feeling?</Label>
            <div className="grid grid-cols-3 gap-2">
              {MOOD_QUICK_OPTIONS.map(option => {
                const Icon = option.icon;
                const isSelected = moodQuick === option.value;
                return (
                  <button
                    key={option.value}
                    onClick={() => setMoodQuick(option.value)}
                    className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1.5 ${
                      isSelected ? option.color + ' border-current' : 'border-transparent bg-muted/30 hover:bg-muted/50'
                    }`}
                    data-testid={`mood-quick-${option.value}`}
                  >
                    <Icon className="w-6 h-6" />
                    <span className="text-xs font-medium">{option.label}</span>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Detailed Mood & Cravings */}
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardContent className="p-5 space-y-5">
            <div className="space-y-3">
              <Label className="text-sm font-medium">Mood: {mood}/10 — {moodLabels[mood]}</Label>
              <Slider value={[mood]} onValueChange={v => setMood(v[0])} max={10} min={1} step={1} className="py-2" data-testid="mood-slider" />
              <div className="flex justify-between text-xs text-muted-foreground"><span>Struggling (1-3)</span><span>Okay (4-6)</span><span>Good (7-10)</span></div>
            </div>

            <div className="space-y-3">
              <Label className="text-sm font-medium">Cravings: {cravings}/10</Label>
              <Slider value={[cravings]} onValueChange={v => setCravings(v[0])} max={10} min={0} step={1} className="py-2" data-testid="cravings-slider" />
              <div className="flex justify-between text-xs text-muted-foreground"><span>None</span><span>Severe</span></div>
            </div>
          </CardContent>
        </Card>

        {/* Accountability Tracker */}
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardContent className="p-5">
            <Label className="text-sm font-medium mb-3 block">Today I...</Label>
            <div className="space-y-2">
              {ACCOUNTABILITY_ITEMS.map(item => (
                <button
                  key={item.key}
                  onClick={() => toggleAccountability(item.key)}
                  className={`w-full p-3 rounded-xl border transition-all flex items-center justify-between ${
                    accountability[item.key] 
                      ? 'bg-teal-50 border-teal-200 text-teal-800' 
                      : 'bg-muted/30 border-transparent hover:bg-muted/50'
                  }`}
                  data-testid={`accountability-${item.key}`}
                >
                  <span className="text-sm">{item.label}</span>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-all ${
                    accountability[item.key] ? 'bg-teal-600 text-white' : 'bg-muted'
                  }`}>
                    {accountability[item.key] && <Check size={12} />}
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Goals Met */}
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardContent className="p-5">
            <button
              onClick={() => setGoalsMet(!goalsMet)}
              className={`w-full p-4 rounded-xl border-2 transition-all flex items-center justify-between ${
                goalsMet 
                  ? 'bg-emerald-50 border-emerald-300 text-emerald-800' 
                  : 'border-dashed border-muted-foreground/30 hover:border-muted-foreground/50'
              }`}
              data-testid="goals-met-button"
            >
              <span className="font-medium">Did you meet your goals today?</span>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                goalsMet ? 'bg-emerald-600 text-white' : 'bg-muted'
              }`}>
                {goalsMet && <Check size={14} />}
              </div>
            </button>
          </CardContent>
        </Card>

        {/* Reflection */}
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardContent className="p-5 space-y-3">
            <div>
              <Label className="text-sm font-medium mb-1 block">Reflection Prompt</Label>
              <p className="text-sm text-muted-foreground" data-testid="checkin-static-prompt">{goalPrompt}</p>
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Your response (optional)</Label>
              <DictationInput
                value={notes}
                onChange={(cleaned, original) => { setNotes(cleaned); setNotesOriginal(original); }}
                placeholder="Share a few sentences if you want to reflect today..."
                rows={3}
                promptContext={{
                  context: 'check_in',
                  taskName: 'Daily Check-in',
                  taskDescription: `Prompt: ${goalPrompt}. Mood: ${moodQuick || moodLabels[mood] || mood}. Goals met: ${goalsMet ? 'yes' : 'no'}.`,
                  category: 'emotional'
                }}
                data-testid="checkin-notes"
              />
            </div>
          </CardContent>
        </Card>

        {/* Submit */}
        <Button 
          onClick={handleSubmit} 
          disabled={loading} 
          className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2 h-12" 
          data-testid="submit-checkin-button"
        >
          <Send size={16} /> {loading ? 'Submitting...' : 'Submit Check-in'}
        </Button>
      </div>
    </div>
  );
}
